"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
function HeaderMenuConsumer({
  isMobile = false,
  closeMenu
}: {
  isMobile?: boolean;
  closeMenu?: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className={cn("flex gap-6", isMobile ? "flex-col" : "items-center")}>
        <div className="h-5 w-24 bg-muted/50 animate-pulse rounded" />
        <div className="h-5 w-24 bg-muted/50 animate-pulse rounded" />
        <div className="h-5 w-24 bg-muted/50 animate-pulse rounded" />
      </div>;
  }
  const linkClasses = "relative font-header uppercase tracking-widest text-sm py-1 text-foreground/80 hover:text-primary transition-all duration-200 hover:scale-105 block md:inline-block";
  if (isMobile) {
    return <nav className="flex flex-col space-y-6 px-4">
        {menuItems.map((item, i) => <div key={item.id} data-index={i}>
            <Link to={item.href || "#"} className={cn(linkClasses, "w-fit")} onClick={closeMenu}>
              {item.label}
            </Link>
          </div>)}
      </nav>;
  }
  return <nav className="hidden md:flex items-center space-x-10">
      {menuItems.map((item, i) => <div key={item.id} data-index={i}>
          <Link to={item.href || "#"} className={linkClasses}>
            {item.label}
          </Link>
        </div>)}
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="224878" id="header" className={cn("sticky top-0 z-50 w-full transition-all duration-500 bg-secondary/95 backdrop-blur-md border-b border-border/40", isScrolled ? "shadow-md py-1" : "shadow-none py-2")}>
      <div className="container mx-auto px-4 h-20 flex items-center justify-between">
        {}
        <Link to="/" className="flex-shrink-0 z-50">
          <img src="https://fun-bengal.10web.cloud/wp-content/uploads/2026/05/senditfit_header_optimised.png" className="h-12 md:h-15 w-auto object-contain transition-transform duration-300 hover:scale-105" alt="Send It Fit mobile personal training - vintage retro athletic branding logo" loading="lazy" decoding="async" />
        </Link>

        {}
        <div className="hidden md:block">
          <MenuProvider menu_id="16">
            <HeaderMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="flex items-center gap-4">
          
          <Button variant="ghost" size="icon" className="md:hidden z-50 text-foreground hover:bg-primary/10 hover:text-primary transition-colors" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-menu size-7" aria-hidden="true" data-ast-id="f:/src/reusable_sections/Header.tsx|sig:8n2ocj" data-ast-start="4748" data-ast-end="4775"><path d="M4 5h16"></path><path d="M4 12h16"></path><path d="M4 19h16"></path></svg></Button>
        </div>
      </div>

      {}
      <div className={cn("absolute top-full left-0 w-full bg-secondary/98 backdrop-blur-2xl border-b border-border transition-all duration-500 ease-in-out overflow-hidden md:hidden", isMobileMenuOpen ? "max-h-[500px] py-10 opacity-100" : "max-h-0 py-0 opacity-0 border-transparent")}>
        <MenuProvider menu_id="16">
          <HeaderMenuConsumer isMobile closeMenu={() => setIsMobileMenuOpen(false)} />
        </MenuProvider>
        <div className="px-4 mt-8">
          <Button className="w-full bg-primary text-secondary font-header tracking-wider text-lg py-6 rounded-lg shadow-md border-2 border-foreground/5" onClick={() => {
          setIsMobileMenuOpen(false);
          window.location.href = "https://wa.me/6588990728";
        }}>
            GET IN TOUCH
          </Button>
        </div>
      </div>
    </section>;
}